import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TokenService } from '../token/token.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private tokenService: TokenService = inject(TokenService);
  private permissionDictionary = new Map<string, number>();
  private userPermissionsSubject: BehaviorSubject<string[]> =
    new BehaviorSubject<string[]>([]);

  userPermissions = this.userPermissionsSubject.asObservable();

  constructor() {
    this.permissionDictionary.set('PEOPLE_VIEW_MENU', 0);
    this.permissionDictionary.set('PEOPLE_CREATE', 1);
    this.permissionDictionary.set('PEOPLE_EDIT', 2);
    this.permissionDictionary.set('PEOPLE_VIEW', 3);
    this.permissionDictionary.set('PEOPLE_VIEW_PII', 4);
    this.permissionDictionary.set('PEOPLE_RESTORE', 5);
    this.permissionDictionary.set('PEOPLE_DELETE', 6);
    this.permissionDictionary.set('PEOPLE_ACTIVE_AND_INACTIVE', 7);
    this.permissionDictionary.set('PEOPLE_CAN_CREATE_USER', 8);

    this.permissionDictionary.set('CORE_VIEW_MENU', 9);
    this.permissionDictionary.set('CORE_CREATE', 10);
    this.permissionDictionary.set('CORE_EDIT', 11);
    this.permissionDictionary.set('CORE_VIEW', 12);
    this.permissionDictionary.set('CORE_VIEW_PII', 13);
    this.permissionDictionary.set('CORE_RESTORE', 14);
    this.permissionDictionary.set('CORE_DELETE', 15);

    this.permissionDictionary.set('PARTNER_VIEW_MENU', 16);
    this.permissionDictionary.set('PARTNER_CREATE', 17);
    this.permissionDictionary.set('PARTNER_EDIT', 18);
    this.permissionDictionary.set('PARTNER_VIEW', 19);
    this.permissionDictionary.set('PARTNER_VIEW_PII', 20);
    this.permissionDictionary.set('PARTNER_RESTORE', 21);
    this.permissionDictionary.set('PARTNER_DELETE', 22);

    this.permissionDictionary.set('PROFILE_VIEW_MENU', 23);
    this.permissionDictionary.set('PROFILE_CREATE', 24);
    this.permissionDictionary.set('PROFILE_EDIT', 25);
    this.permissionDictionary.set('PROFILE_VIEW', 26);
    this.permissionDictionary.set('PROFILE_VIEW_PII', 27);
    this.permissionDictionary.set('PROFILE_ACTIVATE', 28);
    this.permissionDictionary.set('PROFILE_DEACTIVATE', 29);

    this.permissionDictionary.set('STUDENT_VIEW_MENU', 30);
    this.permissionDictionary.set('STUDENT_CREATE', 31);
    this.permissionDictionary.set('STUDENT_EDIT', 32);
    this.permissionDictionary.set('STUDENT_VIEW', 33);
    this.permissionDictionary.set('STUDENT_VIEW_PII', 34);
    this.permissionDictionary.set('STUDENT_RESTORE', 35);
    this.permissionDictionary.set('STUDENT_DELETE', 36);

    this.permissionDictionary.set('CLASS_VIEW_MENU', 37);
    this.permissionDictionary.set('CLASS_CREATE', 38);
    this.permissionDictionary.set('CLASS_EDIT', 39);
    this.permissionDictionary.set('CLASS_VIEW', 40);
    this.permissionDictionary.set('CLASS_VIEW_PII', 41);
    this.permissionDictionary.set('CLASS_RESTORE', 42);
    this.permissionDictionary.set('CLASS_DELETE', 43);

    this.permissionDictionary.set('FREQUENCY_VIEW_MENU', 44);
    this.permissionDictionary.set('FREQUENCY_CREATE', 45);
    this.permissionDictionary.set('FREQUENCY_EDIT', 46);
    this.permissionDictionary.set('FREQUENCY_VIEW', 47);
    this.permissionDictionary.set('FREQUENCY_VIEW_PII', 48);
    this.permissionDictionary.set('FREQUENCY_RESTORE', 49);
    this.permissionDictionary.set('FREQUENCY_DELETE', 50);

    this.permissionDictionary.set('REPORT_VIEW_MENU', 51);
    this.permissionDictionary.set('REPORT_VIEW', 52);
    this.permissionDictionary.set('REPORT_VIEW_PII', 53);

    this.permissionDictionary.set('REPORT_SUPERVISOR_VIEW_MENU', 54);
    this.permissionDictionary.set('REPORT_SUPERVISOR_CREATE', 55);
    this.permissionDictionary.set('REPORT_SUPERVISOR_EDIT', 56);
    this.permissionDictionary.set('REPORT_SUPERVISOR_VIEW', 57);
    this.permissionDictionary.set('REPORT_SUPERVISOR_VIEW_PII', 58);
    this.permissionDictionary.set('REPORT_SUPERVISOR_RESTORE', 59);
    this.permissionDictionary.set('REPORT_SUPERVISOR_DELETE', 60);
    this.permissionDictionary.set('REPORT_SUPERVISOR_GENERATE_PDF', 61);

    this.permissionDictionary.set('DASHBOARD_VIEW_MENU', 69);
    this.permissionDictionary.set('DASHBOARD_OVERVIEW_VIEW', 70);
    this.permissionDictionary.set('DASHBOARD_FREQUENCY_VIEW', 71);

    this.permissionDictionary.set('PROJECT_CREATE', 72);
    this.permissionDictionary.set('PROJECT_VIEW', 73);
    this.permissionDictionary.set('PROJECT_VIEW_MENU', 74);
  }

  setUserPermissions(): void {
    const token = this.tokenService.getToken();

    if (!token || !token.profile || !token.profile.permissions) {
      return;
    }

    const userPermissions = token.profile.permissions;

    let arrayOfPermissions: string[] = [];
    userPermissions.forEach((permission) => {
      const userPermission = this.getPermissionDescription(permission);

      if(userPermission) {
        arrayOfPermissions.push(userPermission);
      }
    });

    this.userPermissionsSubject.next(arrayOfPermissions);
  }

  getPermissionId(permission: string): number {
    return this.permissionDictionary.get(permission) as number;
  }

  getPermissionDescription(id: number): string | undefined {
    for (const [
      description,
      permissionId,
    ] of this.permissionDictionary.entries()) {
      if (permissionId === id) {
        return description;
      }
    }
    return undefined;
  }

  getUserPermissions(): string[] {
    return this.userPermissionsSubject.getValue();
  }

  hasPermission(permission: string) {
    return this.getUserPermissions().includes(permission);
  }
}
